
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { Form } from "@/components/ui/form";
import { customSolutionsFormSchema, type CustomSolutionsFormValues } from "./formSchema";
import { PersonalInfoFields } from "./form/PersonalInfoFields";
import { RoleSelector } from "./form/RoleSelector";
import { PracticeAreaSelector } from "./form/PracticeAreaSelector";
import { AdditionalInfoFields } from "./form/AdditionalInfoFields";

interface CustomSolutionsFormProps {
  onSubmit: (data: CustomSolutionsFormValues) => Promise<void>;
  onSuccess?: () => void;
}

export function CustomSolutionsForm({ onSubmit, onSuccess }: CustomSolutionsFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<CustomSolutionsFormValues>({
    resolver: zodResolver(customSolutionsFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      role: "",
      practiceAreas: [],
      challenges: "",
      goals: "",
      consent: false,
    },
    mode: "onBlur",
  });

  const handleSubmit = async (values: CustomSolutionsFormValues) => {
    setIsSubmitting(true);
    setFormErrors(false);
    try {
      await onSubmit(values);
      form.reset();
      if (onSuccess) onSuccess();
      
      toast({
        title: "Success!",
        description: "Your custom solutions request has been submitted. We'll be in touch shortly.",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Submission failed",
        description: "There was an issue processing your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = () => {
    setFormErrors(true);
    toast({
      title: "Form incomplete",
      description: "Please fill in all required fields to continue.",
      variant: "destructive",
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit, onError)} className="space-y-5">
        <PersonalInfoFields form={form} />
        <RoleSelector form={form} />
        <PracticeAreaSelector form={form} />
        <AdditionalInfoFields form={form} isSubmitting={isSubmitting} formErrors={formErrors} />
      </form>
    </Form>
  );
}
